/**
 * Content Pricing Configuration
 * 
 * This file contains the token costs for course generation and cookbook creation/unlock.
 * Update these values when you need to adjust pricing.
 * 
 * Pricing Strategy:
 * - Course Creation: 15-18 tokens (based on complexity and length)
 * - Cookbook Creation/Unlock: 5 tokens (fixed)
 */

/**
 * Course Generation Pricing
 * 
 * Base price: 15 tokens
 * Difficulty multipliers:
 * - Beginner: +0 tokens (15 total)
 * - Intermediate: +1 token (16 total)
 * - Advanced: +3 tokens (18 total)
 * 
 * Maximum: 18 tokens
 */
export const COURSE_PRICING = {
  base: 15,
  difficulty: {
    beginner: 0,      // 15 tokens total
    intermediate: 1,  // 16 tokens total
    advanced: 3       // 18 tokens total
  },
  max: 18,
  min: 15
};

/**
 * Cookbook Pricing
 * 
 * All cookbook operations (creation or unlock) cost 5 tokens.
 * Exception: Cookbooks auto-generated with courses are FREE (included with course)
 */
export const COOKBOOK_PRICING = {
  cost: 5,           // Cost for cookbook creation or unlock
  fromCourse: 0      // Auto-generated with course (free)
};

/**
 * Calculate course generation cost based on difficulty
 * @param {string} difficulty - 'beginner', 'intermediate', or 'advanced'
 * @returns {number} Token cost for course generation
 */
export function calculateCourseCost(difficulty) {
  const difficultyMultiplier = COURSE_PRICING.difficulty[difficulty] || 0;
  const cost = COURSE_PRICING.base + difficultyMultiplier;

  // Ensure cost is within bounds
  return Math.max(COURSE_PRICING.min, Math.min(cost, COURSE_PRICING.max));
}

/**
 * Get cookbook cost
 * @param {boolean} fromCourse - Whether cookbook is auto-generated with a course
 * @returns {number} Token cost for cookbook operation (5 tokens, or 0 if from course)
 */
export function getCookbookCost(fromCourse = false) {
  return fromCourse ? COOKBOOK_PRICING.fromCourse : COOKBOOK_PRICING.cost;
}

/**
 * Estimate course cost based on prompt and settings
 * This provides a pre-generation estimate
 * @param {Object} params - Course generation parameters
 * @param {string} params.difficulty - Course difficulty level
 * @param {string} params.prompt - User's course description
 * @param {string} params.language - Programming language
 * @returns {Object} Estimated cost information
 */
export function estimateCourseCost({ difficulty, prompt, language }) {
  const baseCost = calculateCourseCost(difficulty);

  // Estimate based on prompt length (rough heuristic)
  const promptLength = prompt?.length || 0;
  let lengthBonus = 0;

  if (promptLength > 200) lengthBonus = 1;  // Longer prompts might generate more content
  if (promptLength > 400) lengthBonus = 2;

  // Cap at max
  const estimatedCost = Math.min(baseCost + lengthBonus, COURSE_PRICING.max);

  return {
    estimated: estimatedCost,
    min: COURSE_PRICING.min,
    max: COURSE_PRICING.max,
    base: baseCost,
    difficulty: difficulty
  };
}

/**
 * Calculate actual course cost after generation
 * This should be called after course is generated to get the final cost
 * @param {Object} params - Course generation result
 * @param {string} params.difficulty - Course difficulty level
 * @param {number} params.lessonCount - Number of lessons in course
 * @param {number} params.subLessonCount - Total number of sub-lessons
 * @param {number} params.codeSnippetCount - Total number of code snippets
 * @returns {number} Actual token cost
 */
export function calculateActualCourseCost({
  difficulty,
  lessonCount = 0,
  subLessonCount = 0,
  codeSnippetCount = 0
}) {
  const baseCost = calculateCourseCost(difficulty);

  // Add complexity bonuses based on actual content
  let complexityBonus = 0;

  // Length-based bonus
  if (lessonCount > 10) complexityBonus += 1;
  if (lessonCount > 15) complexityBonus += 2;

  // Content density bonus
  const avgSubLessonsPerLesson = lessonCount > 0 ? subLessonCount / lessonCount : 0;
  if (avgSubLessonsPerLesson > 5) complexityBonus += 1;

  // Code snippet density
  if (codeSnippetCount > 20) complexityBonus += 1;

  // Cap at max
  return Math.min(baseCost + complexityBonus, COURSE_PRICING.max);
}

/**
 * Pricing display helpers
 */
export const PRICING_DISPLAY = {
  course: {
    range: `${COURSE_PRICING.min}-${COURSE_PRICING.max} tokens`,
    byDifficulty: {
      beginner: `${calculateCourseCost('beginner')} tokens`,
      intermediate: `${calculateCourseCost('intermediate')} tokens`,
      advanced: `${calculateCourseCost('advanced')} tokens`
    }
  },
  cookbook: {
    cost: `${COOKBOOK_PRICING.cost} tokens`,
    fromCourse: 'Free (included with course)'
  }
};

